import { SignalProp, unwrapNetworkResponse } from './fetch-core.ts';
import { makeUnauthenticatedNetworkRequest } from './aws-fetch.ts';

const searchBaseUrl = import.meta.env.VITE_PUBLIC_API_GATEWAY_URL as string;
export const propertySearchUrl = `/land/search`;

export enum LandSearchType {
  PROPERTY = 'PROPERTY',
  MLS = 'MLS',
}

export interface PropertySearchResTypeDataAddress {
  address: string;
  city: string;
  county: string;
  state: string;
  street: string;
  zip: string;
}

export interface PropertySearchResTypeData {
  id: string;
  apn: string;
  name?: string;
  bedrooms?: string;
  bathrooms?: number;
  basePriceRange?: number[];
  lotSquareFeet: number;
  mlsStatus: string;
  lastUpdateDate: string;
  mlsHasPhotos: boolean;
  mlsDaysOnMarket: number;
  mlsListingPrice: number;
  lastSaleAmount: string;
  lastSaleDate: string;
  estimatedValue: number;
  address: PropertySearchResTypeDataAddress;
  latitude: number;
  longitude: number;
  mlsSold: boolean;
  agentNotes?: string;
  photosList?: string[];
  primaryListingImageUrl?: string;
  getGoogleStreetViewImageUrl?: string;
  moveForward?: boolean;
}

export interface PropertySearchResType {
  data: PropertySearchResTypeData[];
  resultCount: number;
  resultIndex: number;
  recordCount: number;
  searchType: LandSearchType;
}

export interface PropertySearchReqTypeBody {
  id?: number | string;
  apn?: string;
  state?: string;
  zip?: string;
  county?: string;
  city?: string;
  address?: string;
  mlsListingPriceMin?: number;
  mlsListingPriceMax?: number;
  lotSizeMin?: number;
  lotSizeMax?: number;
  includeNonVacantLots?: boolean;
  mlsPending?: boolean;
  mlsCancelled?: boolean;
  size?: number;
  vacant?: boolean;
  resultIndex?: number;
}

export interface PropertySearchReqType {
  body: PropertySearchReqTypeBody;
}

export const propertySearch = async ({
  signal,
  body,
}: PropertySearchReqType & SignalProp) => {
  const res = await makeUnauthenticatedNetworkRequest({
    method: 'POST',
    baseUrl: searchBaseUrl,
    relativeUrl: propertySearchUrl,
    signal,
    body,
  });

  // TODO: Hopefully placeholder, should probably be removed and moved to the BE
  if (res.status === 404) {
    return {
      data: [],
      resultCount: 0,
      resultIndex: 0,
      recordCount: 0,
      searchType: LandSearchType.PROPERTY,
    } as PropertySearchResType;
  }
  const data = await unwrapNetworkResponse<PropertySearchResType>(res);
  return data;
};

export interface AutocompleteResTypeData {
  id: string;
  apn: string;
  state: string;
  stateId: string;
  zip: string;
  county: string;
  countyId: string;
  city: string;
  street: string;
  searchType: string;
  address: string;
  fips: string;
  latitude: number;
  longitude: number;
  title: string;
}

export interface AutocompleteResType {
  search: string;
  data: AutocompleteResTypeData[];
}

export interface AutocompleteReqType {
  body: { search: string };
}

export const getAutocompleteUrl = `/land/search/autocomplete`;

export const getAutocomplete = async ({
  signal,
  body,
}: SignalProp & AutocompleteReqType) => {
  const res = await makeUnauthenticatedNetworkRequest({
    method: 'POST',
    baseUrl: searchBaseUrl,
    relativeUrl: getAutocompleteUrl,
    signal,
    body,
  });

  const data = await unwrapNetworkResponse<AutocompleteResType>(res);
  return data;
};
